import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.setupGallery()
  }

  setupGallery() {
    this.gallery = this.element.querySelector('.cycle-slideshow')
    this.pager = this.element.querySelector('#gallery_pager_container')

    if (!this.gallery) {
      setTimeout(() => this.setupGallery(), 100)
      return
    }

    if (typeof $ === 'undefined') {
      setTimeout(() => this.setupGallery(), 100)
      return
    }

    const $gallery = $(this.gallery)
    const images = $gallery.find('img')

    if (images.length === 0) {
      setTimeout(() => this.setupGallery(), 100)
      return
    }

    this.totalSlides = images.length
    this.currentSlide = 0

    this.initializeAccessibility($gallery)
  }

  initializeAccessibility($gallery) {
    this.createScreenReaderAnnouncer()
    this.setupCycleEventHandlers($gallery)
    this.setupKeyboardNavigation($gallery)
    this.setupPagerClicks()
  }

  createScreenReaderAnnouncer() {
    if (!this.element.querySelector('.sr-only[aria-live]')) {
      const announcer = document.createElement('div')
      announcer.className = 'sr-only'
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      this.gallery.appendChild(announcer)
      this.announcer = announcer
    }
  }

  setupCycleEventHandlers($gallery) {
    $gallery.on('cycle-after', (event, opts) => {
      this.currentSlide = opts.currSlide
      this.announceSlide()
      this.updatePagerAttributes()
    })

    $gallery.on('cycle-initialized', (event, opts) => {
      this.currentSlide = opts.currSlide || 0
      this.announceSlide()
      this.updatePagerAttributes()
    })
  }

  setupKeyboardNavigation($gallery) {
    document.addEventListener('keydown', (e) => {
      if (this.isInputFocused()) return

      switch(e.code) {
        case 'ArrowLeft':
          e.preventDefault()
          $gallery.cycle('prev')
          break
        case 'ArrowRight':
          e.preventDefault()
          $gallery.cycle('next')
          break
        case 'Home':
          if (e.ctrlKey) {
            e.preventDefault()
            $gallery.cycle('goto', 0)
          }
          break
        case 'End':
          if (e.ctrlKey) {
            e.preventDefault()
            $gallery.cycle('goto', this.totalSlides - 1)
          }
          break
      }
    })

    this.gallery.addEventListener('keydown', (e) => {
      switch(e.code) {
        case 'Space':
        case 'Enter':
          e.preventDefault()
          $gallery.cycle('toggle')
          break
        case 'ArrowUp':
          e.preventDefault()
          $gallery.cycle('prev')
          break
        case 'ArrowDown':
          e.preventDefault()
          $gallery.cycle('next')
          break
      }
    })

    this.gallery.addEventListener('mouseenter', () => {
      this.gallery.focus()
    })
  }

  setupPagerClicks() {
    if (!this.pager) return

    this.pager.addEventListener('click', (e) => {
      const dot = e.target.closest('.cycle-pager-dot')
      if (dot) {
        e.preventDefault()
        const allDots = this.pager.querySelectorAll('.cycle-pager-dot')
        const slideIndex = Array.from(allDots).indexOf(dot)

        if (slideIndex >= 0 && slideIndex < this.totalSlides) {
          $(this.gallery).cycle('goto', slideIndex)
        }
      }
    })

    this.pager.addEventListener('keydown', (e) => {
      const dot = e.target.closest('.cycle-pager-dot')
      if (dot && (e.code === 'Enter' || e.code === 'Space')) {
        e.preventDefault()
        const allDots = this.pager.querySelectorAll('.cycle-pager-dot')
        const slideIndex = Array.from(allDots).indexOf(dot)
        if (slideIndex >= 0 && slideIndex < this.totalSlides) {
          $(this.gallery).cycle('goto', slideIndex)
        }
      }
    })
  }

  updatePagerAttributes() {
    if (!this.pager) return

    const dots = this.pager.querySelectorAll('.cycle-pager-dot')

    dots.forEach((dot, index) => {
      if (!dot.hasAttribute('aria-label')) {
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`)
      }

      if (!dot.hasAttribute('role')) {
        dot.setAttribute('role', 'button')
      }

      if (!dot.hasAttribute('tabindex')) {
        dot.setAttribute('tabindex', '0')
      }
    })
  }

  announceSlide() {
    if (!this.announcer) return

    const images = this.gallery.querySelectorAll('img')
    const currentImage = images[this.currentSlide]
    if (currentImage) {
      const altText = currentImage.alt || 'Image'
      const slideNum = this.currentSlide + 1
      this.announcer.textContent = `Slide ${slideNum} of ${this.totalSlides}: ${altText}`
    }
  }

  isInputFocused() {
    const activeElement = document.activeElement
    return activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    )
  }
}
